import { createContext, useContext, useEffect } from 'react'

export interface BreadcrumbItem {
	label: string
	path?: string
}

interface BreadcrumbContextValue {
	breadcrumbs: BreadcrumbItem[]
	setBreadcrumbs: (items: BreadcrumbItem[]) => void
}

export const BreadcrumbContext = createContext<BreadcrumbContextValue | undefined>(undefined)

export const useBreadcrumbContext = () => {
	const context = useContext(BreadcrumbContext)
	if (!context) {
		throw new Error('useBreadcrumbContext must be used within BreadcrumbProvider')
	}
	return context
}

/**
 * Hook to set breadcrumbs for a page
 * Automatically cleans up on unmount
 *
 * Trails start at the trip name (the overview is the root and shows no trail).
 *
 * @example
 * useBreadcrumbs([
 *   { label: trip.name, path: `/trips/${tripId}` },
 *   { label: 'Itinerary' }
 * ])
 */
export const useBreadcrumbs = (items: BreadcrumbItem[]) => {
	const { setBreadcrumbs } = useBreadcrumbContext()
	const serializedItems = JSON.stringify(items)

	useEffect(() => {
		setBreadcrumbs(JSON.parse(serializedItems) as BreadcrumbItem[])

		// Cleanup: clear breadcrumbs when component unmounts
		return () => {
			setBreadcrumbs([])
		}
	}, [serializedItems, setBreadcrumbs])
}
